import { Environment } from "@amzn/xb-hooks";
import { ExperienceProviderConfig } from "@amzn/aws-tc-xb-xps";
import registry from "./experience-providers.json";

type XpsRegistryEntry = Partial<
  Record<Environment, { LiveComponentsEndpoint: string }>
> & {
  name: string;
};

/**
 * Fetch the global registry of xps endpoints, but allow the caller to override/extend the
 * list to enable development or customization beyond what XB Provides. This is what the
 * LiveComponent uses to fetch from the XPS endpoints.
 *
 * While a "local" endpoint for an XPS doesn't make a lot of sense in a
 * global registry, the actual usage of this will likely want the "beta" endpoints when
 * running in the "local" stage.
 *
 * @param env - The environment of the XB Application
 * @param overrides - Overrides the XB Application adds for testing or extension beyond XB
 * @returns the resolved ExperienceProviderConfigs to be used for LiveComponent lookups
 */
export function getXpsConfig(
  env: Environment,
  overrides: ExperienceProviderConfig[] = []
): ExperienceProviderConfig[] {
  const remappedEnv = remapEnvironment(env);
  return (registry as XpsRegistryEntry[])
    .filter((xps) => xps[remappedEnv] !== undefined)
    .map((xps) => ({
      name: xps.name,
      componentEndpoint: xps[remappedEnv]!.LiveComponentsEndpoint,
    }))
    .filter((xps) => overrides.every((override) => override.name !== xps.name))
    .concat(...overrides);
}

export const remapEnvironment = (env: Environment): Environment => {
  if (env === "local") {
    return "beta";
  }
  return env;
};
